import { useCallback, useMemo } from "react";
import {
  sortRows,
  type SortValueGetters,
  type TableSort,
} from "hooks/useTableSort";
import { z } from "zod";
import { useNavigationQuery } from "./NavigationQueryProvider";
import type { Serialiser } from "hooks/serialisers/types";
import { jsonQuery } from "./queryCodecs";

const sortQuery = jsonQuery(
  z
    .object({ column: z.string(), direction: z.enum(["asc", "desc"]) })
    .nullable()
);
export const useNavigationTableSort = <
  Row,
  Column extends string,
  Rows extends readonly Row[] | undefined,
>({
  key,
  rows,
  sortValueGetters,
  initialSort,
}: {
  key: string;
  rows: Rows;
  sortValueGetters: SortValueGetters<Row, Column>;
  initialSort: NoInfer<TableSort<Column>> | null;
}) => {
  const codec = useMemo<Serialiser<TableSort<Column> | null, never>>(
    () => ({
      format: sortQuery.format,
      parse: (text) => {
        const sort = sortQuery.parse(text);
        const isColumn = (column: string): column is Column =>
          Object.hasOwn(sortValueGetters, column);
        if (!sort.valid) return sort;
        if (sort.value == null) return { valid: true, value: null };
        return isColumn(sort.value.column)
          ? {
              valid: true,
              value: {
                column: sort.value.column,
                direction: sort.value.direction,
              },
            }
          : { valid: false };
      },
    }),
    [sortValueGetters]
  );
  const [sort, setSort] = useNavigationQuery(
    key,
    initialSort,
    codec,
    "replace"
  );
  const sortedRows = useMemo(
    () =>
      (rows &&
        sortRows({ rows, sortValueGetters, sort })) as Rows extends undefined
        ? undefined
        : readonly Row[],
    [rows, sortValueGetters, sort]
  );
  const toggleSort = useCallback(
    (column: Column) => {
      setSort((current) => ({
        column,
        direction:
          current?.column === column && current.direction === "asc"
            ? "desc"
            : "asc",
      }));
    },
    [setSort]
  );
  return { sortedRows, sort, toggleSort };
};
